/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';

import { AtpDeliveryInfo } from 'features/atp/components/desktop/AtpDeliveryInfo';
import { getPostalCode, getProductDeliveryDate, getProductDeliveryDates } from 'features/atp/api';
import { AtpDeliveryInfoResponse } from 'features/atp/types/AtpDeliveryInfoResponse';
import { getLatestDeliveryInfoForKitSkus } from 'features/atp/utilities';
import { AtpMultipleDeliveryInfoParams } from 'features/atp/types/AtpMultipleDeliveryInfoParams';
import { AtpMultipleDeliveryInfoAndRenderParams } from 'features/atp/types/AtpMultipleDelieryInfoAndRenderParams';
import { AtpInitDeliveryInfoParams } from 'features/atp/types/AtpInitDeliveryInfoParams';
import { AtpInitDeliveryInfoForKitSkusParams } from 'features/atp/types/AtpInitDeliveryInfoForKitSkusParams';
import { AtpInitMultipleDeliveryInfoParams } from 'features/atp/types/AtpInitMultipleDeliveryInfoParams';
import { AtpRenderDeliveryInfoParams } from 'features/atp/types/AtpRenderDeliveryInfoParams';
import { FALLBACK_CALLOUT_TEXT } from 'features/atp/config';

async function renderAtpDeliveryInfo ({
	rootElement,
	deliveryDate,
	deliveryDateLabel,
	fallbackDateLabel,
	fallbackCalloutText,
	showShipsInVerbiage,
	cutoffDate,
	cutoffDateCalloutText,
	zipCode,
	onZipChangeSuccess
}: AtpRenderDeliveryInfoParams): Promise<void> {

	// Use "Arrives" fallback if there's no ATP delivery date and customer zip code is known
	if (fallbackDateLabel && !deliveryDateLabel && !showShipsInVerbiage && zipCode) {

		const { atpDeliveryFallbackLabel } = window.lp.pageData;
		if (atpDeliveryFallbackLabel) {

			fallbackDateLabel = atpDeliveryFallbackLabel;
			fallbackCalloutText = FALLBACK_CALLOUT_TEXT.ARRIVES;

			// No cutoff text for "Arrives" fallback
			cutoffDate = '';
			cutoffDateCalloutText = '';

		}

	}

	unmountComponentAtNode(rootElement);

	rootElement.classList.remove('skeleton', 'hidden');

	render(
		<AtpDeliveryInfo
			deliveryDate={deliveryDate}
			deliveryDateLabel={deliveryDateLabel}
			fallbackDateLabel={fallbackDateLabel}
			fallbackCalloutText={fallbackCalloutText}
			zipCode={zipCode}
			cutoffDate={cutoffDate}
			cutoffDateCalloutText={cutoffDateCalloutText}
			onZipChangeSuccess={onZipChangeSuccess}
			showIcon
			showNoAtpAndFallbackError
		/>,
		rootElement
	);

}

async function getMultipleDeliveryInfoAndRender ({
	products,
	zipCode,
	rootElement,
	onZipChangeSuccess,
	fallbackDateLabel,
	fallbackCalloutText,
	cutoffDateCalloutText,
	isInit = false
}: AtpMultipleDeliveryInfoAndRenderParams): Promise<AtpDeliveryInfoResponse[]> {

	let selectedShortSku = '';
	const shortSkus = products.map((p) => {

		if (p.isSelected && !selectedShortSku) {

			selectedShortSku = p.shortSku;

		}
		return p.shortSku;

	});

	if (shortSkus.length === 0) {

		throw new Error('No shortSkus passed to getMultipleDeliveryInfoAndRender function call.');

	}

	if (!selectedShortSku) {

		throw new Error('No selected shortSku passed to getMultipleDeliveryInfoAndRender function call.');

	}

	const deliveryDateResponse = await getProductDeliveryDates(shortSkus, !isInit);

	if (!deliveryDateResponse) {

		throw new Error('No response from getProductDeliveryDates API call.');

	}

	const models: AtpDeliveryInfoResponse[] = deliveryDateResponse.ResponseModel || [];
	const model = models.find(({ ShortSku }) => ShortSku === selectedShortSku);
	const showShipsInVerbiage = model?.ShowShipsInVerbiage || false;

	if (!deliveryDateResponse.IsSuccess) {

		const isInternational = model?.IsInternational || false;
		const error: Error & { data?: any } = new Error(
			`getProductDeliveryDates API call failed: ${deliveryDateResponse.ErrorMessage}`
		);
		error.data = { showShipsInVerbiage, isInternational };
		throw error;

	}

	renderAtpDeliveryInfo({
		rootElement,
		deliveryDate: model?.DeliveryDate,
		deliveryDateLabel: model?.DeliveryDateLabel,
		cutoffDate: model?.CutoffTime,
		showShipsInVerbiage,
		fallbackDateLabel,
		fallbackCalloutText,
		cutoffDateCalloutText,
		zipCode,
		onZipChangeSuccess
	});

	return models;

}

async function initAtpDeliveryInfo ({
	rootElement,
	shortSku,
	isAtpToggleOn,
	fallbackCallback,
	onZipChangeSuccess,
	fallbackDateLabel,
	fallbackCalloutText,
	cutoffDate,
	cutoffDateCalloutText,
	isOpenBox = false
}: AtpInitDeliveryInfoParams): Promise<void> {

	if (!isAtpToggleOn && !fallbackDateLabel) {

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}
		return;

	}

	let zipCode = '';
	let showShipsInVerbiage = false;
	let isInternational = false;
	const renderFallbackDeliveryInfo = (): void | null => {

		if (rootElement) {

			renderAtpDeliveryInfo({
				rootElement,
				deliveryDate: '',
				deliveryDateLabel: '',
				fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
				fallbackCalloutText,
				showShipsInVerbiage,
				cutoffDate,
				cutoffDateCalloutText,
				zipCode,
				onZipChangeSuccess
			});

			return;

		}

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}

	};

	try {

		if (!rootElement || !shortSku) {

			throw new Error('ATP root element does not exist or shortSku is empty or not defined.');

		}

		const zipCodeResponse = await getPostalCode();
		if (!zipCodeResponse) {

			throw new Error('No response from getPostalCode API call.');

		}

		if (!zipCodeResponse.IsSuccess) {

			throw new Error(`getPostalCode API call failed: ${zipCodeResponse.ErrorMessage}`);

		}

		zipCode = zipCodeResponse.ResponseModel.PostalCode;

		if (!isAtpToggleOn) {

			renderFallbackDeliveryInfo();
			return;

		}

		if (!zipCode) {

			throw new Error('No zip code returned from getPostalCode API call.');

		}

		const deliveryDateResponse = await getProductDeliveryDate(shortSku, isOpenBox);
		if (!deliveryDateResponse) {

			throw new Error('No response from getProductDeliveryDate API call.');

		}

		const model = deliveryDateResponse.ResponseModel;

		if (!deliveryDateResponse.IsSuccess) {

			showShipsInVerbiage = model?.ShowShipsInVerbiage || false;
			isInternational = model?.IsInternational || false;
			throw new Error(`getProductDeliveryDate API call failed: ${deliveryDateResponse.ErrorMessage}`);

		}

		renderAtpDeliveryInfo({
			rootElement,
			deliveryDate: model.DeliveryDate,
			deliveryDateLabel: model.DeliveryDateLabel,
			cutoffDate: model.CutoffTime,
			showShipsInVerbiage: model.ShowShipsInVerbiage,
			cutoffDateCalloutText,
			fallbackDateLabel,
			fallbackCalloutText,
			zipCode,
			onZipChangeSuccess
		});

	} catch (error) {

		console.error(error);
		renderFallbackDeliveryInfo();

	}

}

async function initAtpDeliveryInfoForKitSkus ({
	rootElement,
	kitSkuComponents,
	isAtpToggleOn,
	fallbackCallback,
	onZipChangeSuccess,
	fallbackDateLabel,
	fallbackCalloutText,
	cutoffDate,
	cutoffDateCalloutText
}: AtpInitDeliveryInfoForKitSkusParams): Promise<void> {

	if (!isAtpToggleOn && !fallbackDateLabel) {

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}
		return;

	}

	let zipCode = '';
	let showShipsInVerbiage = false;
	let isInternational = false;
	const renderFallbackDeliveryInfo = (): void | null => {

		if (rootElement) {

			renderAtpDeliveryInfo({
				rootElement,
				deliveryDate: '',
				deliveryDateLabel: '',
				fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
				fallbackCalloutText,
				showShipsInVerbiage,
				cutoffDate,
				cutoffDateCalloutText,
				zipCode,
				onZipChangeSuccess
			});

			return;

		}

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}

	};

	try {

		if (!rootElement || !kitSkuComponents || kitSkuComponents.length === 0) {

			throw new Error('ATP root element does not exist or array of kitSkuComponents is empty or not defined.');

		}

		const zipCodeResponse = await getPostalCode();
		if (!zipCodeResponse) {

			throw new Error('No response from getPostalCode API call.');

		}

		if (!zipCodeResponse.IsSuccess) {

			throw new Error(`getPostalCode API call failed: ${zipCodeResponse.ErrorMessage}`);

		}

		zipCode = zipCodeResponse.ResponseModel.PostalCode;

		if (!isAtpToggleOn) {

			renderFallbackDeliveryInfo();
			return;

		}

		if (!zipCode) {

			throw new Error('No zip code returned from getPostalCode API call.');

		}

		const deliveryDateResponse = await getProductDeliveryDates(kitSkuComponents, false);

		if (!deliveryDateResponse) {

			throw new Error('No response from getProductDeliveryDates API call.');

		}

		const models: AtpDeliveryInfoResponse[] = deliveryDateResponse.ResponseModel || [];

		if (!deliveryDateResponse.IsSuccess) {

			if (models.length > 0) {

				// flags will always be the same for all KitSKUs
				showShipsInVerbiage = models[0].ShowShipsInVerbiage;
				isInternational = models[0].IsInternational;

			}
			throw new Error(`getProductDeliveryDates API call failed: ${deliveryDateResponse.ErrorMessage}`);

		}

		const latestDeliveryInfo = getLatestDeliveryInfoForKitSkus(models, kitSkuComponents);

		renderAtpDeliveryInfo({
			rootElement,
			deliveryDate: latestDeliveryInfo?.DeliveryDate,
			deliveryDateLabel: latestDeliveryInfo?.DeliveryDateLabel,
			cutoffDate: latestDeliveryInfo?.CutoffTime,
			showShipsInVerbiage: latestDeliveryInfo?.ShowShipsInVerbiage || false,
			cutoffDateCalloutText,
			fallbackDateLabel,
			fallbackCalloutText,
			zipCode,
			onZipChangeSuccess
		});

	} catch (error) {

		console.error(error);
		renderFallbackDeliveryInfo();

	}

}

async function initMultipleAtpDeliveryInfo ({
	rootElement,
	products,
	isAtpToggleOn,
	fallbackCallback,
	onZipChangeSuccess,
	fallbackDateLabel,
	fallbackCalloutText,
	cutoffDate,
	cutoffDateCalloutText
}: AtpInitMultipleDeliveryInfoParams): Promise<AtpMultipleDeliveryInfoParams> {

	if (!isAtpToggleOn && !fallbackDateLabel) {

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}
		return null;

	}

	let zipCode = '';
	let showShipsInVerbiage = false;
	let isInternational = false;
	const renderFallbackDeliveryInfo = (): AtpMultipleDeliveryInfoParams => {

		if (rootElement) {

			renderAtpDeliveryInfo({
				rootElement,
				deliveryDate: '',
				deliveryDateLabel: '',
				fallbackDateLabel: isInternational ? '' : fallbackDateLabel,
				fallbackCalloutText,
				showShipsInVerbiage,
				cutoffDate,
				cutoffDateCalloutText,
				zipCode,
				onZipChangeSuccess
			});

			return {
				deliveryInfo: null,
				zipCode,
				showShipsInVerbiage,
				isInternational
			};

		}

		if (typeof fallbackCallback === 'function') {

			fallbackCallback();

		}

		return {
			deliveryInfo: null,
			zipCode,
			showShipsInVerbiage,
			isInternational
		};

	};

	try {

		if (!rootElement || !products || products.length === 0) {

			throw new Error('ATP root element does not exist or array of shortSkus is empty or not defined.');

		}

		const zipCodeResponse = await getPostalCode();
		if (!zipCodeResponse) {

			throw new Error('No response from getPostalCode API call.');

		}

		if (!zipCodeResponse.IsSuccess) {

			throw new Error(`getPostalCode API call failed: ${zipCodeResponse.ErrorMessage}`);

		}

		zipCode = zipCodeResponse.ResponseModel.PostalCode;

		if (!isAtpToggleOn) {

			return renderFallbackDeliveryInfo();

		}

		if (!zipCode) {

			throw new Error('No zip code returned from getPostalCode API call.');

		}

		const deliveryInfo = await getMultipleDeliveryInfoAndRender({
			products,
			zipCode,
			rootElement,
			onZipChangeSuccess,
			fallbackDateLabel,
			fallbackCalloutText,
			cutoffDateCalloutText,
			isInit: true
		});

		return {
			deliveryInfo,
			zipCode,
			showShipsInVerbiage: deliveryInfo[0]?.ShowShipsInVerbiage || false,
			isInternational: deliveryInfo[0]?.IsInternational || false
		};

	} catch (error) {

		console.error(error);
		const { data } = error;
		showShipsInVerbiage = data?.showShipsInVerbiage || false;
		isInternational = data?.isInternational || false;
		return renderFallbackDeliveryInfo();

	}

}

export {
	getMultipleDeliveryInfoAndRender,
	renderAtpDeliveryInfo,
	initAtpDeliveryInfo,
	initAtpDeliveryInfoForKitSkus,
	initMultipleAtpDeliveryInfo
};
